import { Copy, X as Cancel, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import type { Run } from "@/api/runs";
import { useCancelRun, useRetryRun } from "@/api/submission";
import { elapsedLabel, tileCountLabel } from "@/components/runs/run-header-meta";
import { RunStateBadge } from "@/components/runs/run-state-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const TERMINAL_FAILURE = new Set([
  "failed_validation",
  "failed_system",
  "failed_contract",
  "failed_scientific",
]);

const ACTIVE = new Set([
  "validating",
  "cached",
  "staging_inputs",
  "dispatched",
  "running",
  "collecting_outputs",
  "validating_outputs",
]);

/**
 * Run-detail page header — mono breadcrumbs, title row (workload, state
 * badge, tile-count chip), the right-aligned mono meta cluster, and the
 * action rail (Cancel / Retry / copy run_id).
 *
 * The cancel + retry mutations live behind useCancelRun/useRetryRun
 * (B2). Buttons gate on the run's current state per the lifecycle:
 *   - Cancel: visible only on active states.
 *   - Retry: visible only on terminal-failure states.
 *
 * CHROME ONLY (Tier-2 chrome 4/4). No state derivation moved: the
 * badge is still `RunStateBadge`, which owns the fitted/failed/partial
 * distinctions, and nothing here can make an error read as "no data"
 * (flowstack#923) because nothing here reads the status except those
 * two pre-existing sets.
 *
 * WHAT THE CLUSTER SHOWS is bounded by what the API sends, not by the
 * mock: `run-header-meta.ts` documents why cost is absent entirely and
 * why elapsed appears only for a run that both started and finished.
 */
export function RunHeader({ run }: { run: Run }): JSX.Element {
  const workload = run.flow_name ?? run.tile_name ?? run.run_id;
  const cancel = useCancelRun();
  const retry = useRetryRun();
  const isActive = ACTIVE.has(run.status);
  const isFailed = TERMINAL_FAILURE.has(run.status);
  const elapsed = elapsedLabel(run);
  const submitted = new Date(run.submitted_at).toLocaleString();

  const copyRunId = (): void => {
    navigator.clipboard
      .writeText(run.run_id)
      .then(() => toast.success("Run ID copied"))
      .catch(() => toast.error("Failed to copy run ID"));
  };

  return (
    <div className="flex flex-col gap-3 border-b border-border-subtle pb-4">
      {/* Breadcrumbs are MONO, per the mock's `.crumbs` and the idiom
          surfaces 2-4 established: an identifier path reads as machine
          text, and the run id inside it already did. `text-xs` is the
          scale step, not a literal — `type-scale.test.ts` fails a second
          copy of any size number in `src`, so the size lives in
          `tailwind.config.js` and the call site names the step. Not
          `.label-micro`: that treatment is uppercase, and upper-casing a
          project slug or a run id would misrepresent an identifier that
          is case-sensitive on the wire. */}
      <div className="flex flex-wrap items-center gap-2 font-mono text-xs text-muted-foreground">
        {run.project_slug && (
          <>
            <Link to={`/projects/${run.project_slug}`} className="hover:text-foreground">
              {run.project_slug}
            </Link>
            <span aria-hidden>·</span>
          </>
        )}
        <Link to="/runs" className="hover:text-foreground">
          Runs
        </Link>
        <span aria-hidden>·</span>
        <span>{run.run_id}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              aria-label="Copy run ID"
              onClick={copyRunId}
            >
              <Copy className="h-3 w-3" aria-hidden />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Copy run ID</TooltipContent>
        </Tooltip>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        {/* Title row: workload, state, tile count — one line, as the mock
            has it, rather than the title stacked over a second row. The
            `<h1>` steps DOWN from `text-3xl` (26px) to `text-2xl` (21px):
            `3xl` was chosen against the pre-density scale, and 21px is
            both the mock's `h1` and the step that lets the badge and the
            chip sit beside it without the row wrapping at the widths a
            flow name reaches. Named as the step, never as the number. */}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-foreground">
            {workload}
          </h1>
          <RunStateBadge state={run.status} size="md" />
          {/* Tile count — the mock's neutral `.chip` beside the state
              chip. `invocations` is the API's own per-Tile rollup, so
              this is a count of rows received, not a guess from the
              flow's shape. */}
          <Badge
            variant="outline"
            className="border-border-subtle bg-surface-2 font-medium text-muted-foreground"
          >
            {tileCountLabel(run)}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {/* The mono meta cluster. The mock's third figure is a cost;
              the run payload carries no cost field, so there is nothing
              to render and nothing to qualify — see `run-header-meta.ts`
              for why a bare figure would repeat flowstack#1089. */}
          {/* Each figure carries an `sr-only` label. The cluster reads by
              position sighted — elapsed, submitted, who — and position is
              exactly what a screen reader does not get, so a bare
              timestamp beside a bare address would be two unlabelled
              strings. The visible row loses the words the old two-line
              header spelled out ("submitted … by …"); the accessible name
              keeps them. */}
          <div className="flex flex-wrap items-center gap-4 font-mono text-xs text-muted-foreground">
            {elapsed && (
              <span title="Wall time between the run starting and reaching a terminal state">
                <span className="sr-only">Elapsed </span>
                {elapsed}
              </span>
            )}
            <span>
              <span className="sr-only">Submitted </span>
              <time dateTime={run.submitted_at}>{submitted}</time>
            </span>
            <span>
              <span className="sr-only">Submitted by </span>
              {run.submitted_by}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isActive && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  cancel.mutate(run.run_id, {
                    onSuccess: (data) => toast.success(`Run cancelled (${data.state})`),
                    onError: (err) => toast.error(`Cancel failed: ${(err as Error).message}`),
                  });
                }}
                disabled={cancel.isPending}
              >
                <Cancel className="h-3.5 w-3.5" aria-hidden />
                Cancel
              </Button>
            )}
            {isFailed && (
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  retry.mutate(
                    { runId: run.run_id, projectSlug: run.project_slug },
                    {
                      onSuccess: (data) => toast.success(`Retry queued (${data.run_id})`),
                      onError: (err) => toast.error(`Retry failed: ${(err as Error).message}`),
                    }
                  );
                }}
                disabled={retry.isPending}
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
